import React, { useState, useEffect} from "react";
import DataFetchHookV3 from "../../helper/DataFetchHookV3";
import { Table, PageHeader, Card, Form, Row, Button, Col, Select, Input } from "antd";
import { SearchOutlined, PlusOutlined } from "@ant-design/icons";
import GroupAccess from "../../helper/GroupAccess";
import ExportModalV3 from "../../helper/ExportModalV3";
import CustomPaginationV2 from "../../helper/Table/CustomPaginationV2";
import CustomDateRangeV3 from "../../helper/Table/CustomDateRangeV3";
import {API_TXALERT} from "../../config/config";
import moment from "moment";
import ColumnResizable from "../../helper/Table/ColumnResizable";
import MergeColumns from "../../helper/Table/MergeColumns";
import MerchantList from "../../components/MerchantList";
import RetailerList from "../../components/RetailerList";
import SiteList from "../../components/SiteList";
import createExportDateHandler from "../../helper/createExportDateHandler";

const DATA_URL = API_TXALERT + "/descriptor_enrollments";
const DATA_URL_EXPORT = API_TXALERT + "/descriptor_enrollments/reports";

const { Option } = Select;

const MAX_DATERANGE = 3;
const DEFAULT_DATERANGE = 7;
const DEFAULT_RANGETYPE = "days";
const START_DATE = moment().subtract(DEFAULT_DATERANGE, DEFAULT_RANGETYPE);
const END_DATE = moment();


const DescriptorEnrollments = (props) => {
    const dataFetchHookObj = DataFetchHookV3(DATA_URL,"POST", {
        keyword: "",
        startDate: START_DATE,
        endDate: END_DATE,
        field: null,
        merchant: [],
        retailer: [],
        site: [],
        order: "DESC",
        sort_field: "enrollment_request_id",
        size: 20,
        page: 1,
    });

    const [{ data, totalResult, isLoading, requestData }] = dataFetchHookObj;
    const setRequestData = dataFetchHookObj[2];

    const [dates, setDates] = useState([START_DATE, END_DATE]);
    const [midSelect, setMidSelect] = useState('');
    const [ridSelect, setRidSelect] = useState('');
    const [formData, setFormData] = useState();
    const [currentPage, setCurrentPage] = useState(1);
    const [isDateExceeded, setIsDateExceeded] = useState(false);
    const { t } = props;

    const initFormData = {
        keyword: "",
        dateRange: [START_DATE, END_DATE],
        field: null,
        merchant: [],
        retailer: [],
        site: [],
        order: "DESC",
        sort_field: "enrollment_request_id"
    };

    const [form] = Form.useForm();

    useEffect(() => {
        formValInit();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (pagination, filters, sorter, { currentDataSource }) => {
        var orderBy = 'ASC';
        if(sorter.order === 'descend'){
            orderBy = 'DESC';
        }
        var sortField = 'enrollment_request_id';
        if (sorter.field !== undefined){
            sortField = sorter.field;
        }
        var p = pagination.current;
        if (Object.keys(sorter).length >0 && requestData.sort_field !== sortField){
            p = 1;
            setCurrentPage(1);
        }

        setRequestData({ ...requestData, order: orderBy, sort_field: sortField, page: p, size: pagination.pageSize  });
    };

    const onChangeRetailer = async (value) => {
        await setRidSelect(value)
    }

    const onChangeMerchant = async (value) => {
        await setMidSelect(value)
    }

    const clearAll = () => {
        setRidSelect('');
        setMidSelect('');
        setCurrentPage(1);
        setFormData(initFormData);
        setDates([START_DATE, END_DATE]);
        setRequestData({ ...requestData, ...initFormData, startDate: START_DATE, endDate: END_DATE, page: 1 });
        form.setFieldsValue(initFormData);
    };

    const formValInit = () => {
        setFormData(initFormData);
        form.setFieldsValue(initFormData);
    };

    const formValChange = (chgV, allV) => {
        setFormData(allV);
    };

    const onFinish = () => {
        let startDate = null;
        let endDate = null;
        let searchData = {};

        if (formData) {
            if (dates) {
                startDate = dates[0] ? dates[0] : null;
                endDate = dates[1] ? dates[1] : null;
            }
            searchData = {
                field: formData.field,
                keyword: formData.keyword ? formData.keyword.trim() : null,
                order: formData.order,
                mid: formData.merchant,
                rid: formData.retailer,
                sid: formData.site,
                status: formData.status,
            };
        }
        // Reset Page Index to 1
        setCurrentPage(1);
        setRequestData({ ...requestData, ...searchData, startDate: startDate, endDate: endDate, page: 1 });
    };


    const exportHeadersOptional = [];
    const exportHeaders = [
        { label: "ID", key: "enrollment_request_id" },
        { label: "Merchant Name", key: "merchant_name" },
        { label: "Site Name", key: "site_name" },
        { label: "Products", key: "products" },
        { label: "Status", key: "status" },
        { label: "Request Date", key: "request_date" },
        { label: "Last Activity", key: "last_activity" },
        { label: "Completed At", key: "completed_at" }
    ];

    const columns = [
        {
            dataIndex: "enrollment_request_id",
            key: "enrollment_request_id",
            title: t("tables.fields.enrollment_request_id"),
            width: 100,
            fixed: "left",
            sorter: {
                compare: (a, b) => a.enrollment_request_id - b.enrollment_request_id
            },

            render: (id, record) => (
                <a href={`/descriptorenrollment/${id}`} target="_blank" rel="noopener noreferrer">
                    {id}{" "}
                </a>
            ),
        },
        {
            dataIndex: "merchant_name",
            key: "merchant_name",
            title: t("tables.fields.merchant_name"),
            width: 170,
            ellipsis: true,
            sorter: {
                compare: (a,b) => a.merchant_name.localeCompare(b.merchant_name)
            }
        },
        {
            dataIndex: "site_name",
            key: "site_name",
            title: t("tables.fields.site_name"),
            width: 170,
            ellipsis: true,
            sorter: {
                compare: (a,b) => a.site_name.localeCompare(b.site_name)
            }
        },
        {
            dataIndex: "products",
            key: "products",
            title: t("tables.fields.products"),
            width: 170,
            ellipsis: true,
            sorter: {
                compare: (a,b) => a.products.localeCompare(b.products)
            }
        },
        {
            dataIndex: "status",
            key: "status",
            title: t("tables.fields.status"),
            width: 170,
            ellipsis: true,
            sorter: {
                compare: (a,b) => a.status.localeCompare(b.status)
            }
        },
        {
            dataIndex: "request_date",
            key: "request_date",
            title: t("tables.fields.request_date"),
            width: 150,
            ellipsis: true,
            render: (request_date) => (request_date == null ? '' : moment(request_date).format("YYYY-MM-DD HH:mm:ss")),
            sorter: {
                compare: (a, b) => moment(a.request_date).diff(moment(b.request_date)),
            }
        },
        {
            dataIndex: "last_activity",
            key: "last_activity",
            title: t("tables.fields.last_activity"),
            width: 150,
            ellipsis: true,
            render: (last_activity) => (last_activity === null || last_activity === '' ? '' : moment(last_activity).format("YYYY-MM-DD HH:mm:ss")),
            sorter: {
                compare: (a, b) => moment(a.last_activity).diff(moment(b.last_activity)),
            }
        },
        {
            dataIndex: "completed_at",
            key: "completed_at",
            title: t("tables.fields.completed_at"),
            width: 150,
            ellipsis: true,
            render: (completed_at) => (completed_at === null || completed_at === '' ? '' : moment(completed_at).format("YYYY-MM-DD HH:mm:ss")),
            sorter: {
                compare: (a, b) => moment(a.completed_at).diff(moment(b.completed_at)),
            }
        },
    ];

    /**
     * callback to reflect the changes on dates to the passing request data
     * 
     * @param array values 
     */
    const handleDateChange = createExportDateHandler(requestData);

    const AdvancedSearchForm = () => (
        <div className="search-wrap">
            <Card title={t("tables.actions.search")} size="small">
                <Form form={form} size="large" name="advanced_search" className="ant-advanced-search-form" onValuesChange={formValChange} onFinish={onFinish}>
                    <Row gutter={[16, 16]}>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 8 }} xl={{ span: 6 }}>
                            <Form.Item name="dateRange" rules={[
                                    {
                                        validator: () => {
                                            if(isDateExceeded){
                                                return Promise.reject(t('max_3_months'));
                                            }else if((dates[0] && !dates[1]) || (!dates[0] && dates[1])){
                                                return Promise.reject(t('min_1_day'));
                                            }else{
                                                return Promise.resolve();
                                            }
                                        }
                                    }
                                ]}>
                                <CustomDateRangeV3 t={t} setIsDateExceeded={setIsDateExceeded} state={{ dates, setDates }} unit="months" limit={MAX_DATERANGE} onChange={handleDateChange} />
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }}>
                            <Form.Item name="status">
                                <Select mode="multiple" showArrow="true" maxTagCount="responsive" allowClear style={{ width: "100%" }} placeholder={t("tables.fields.status")}>
                                    <Option value="PENDING">PENDING</Option>
                                    <Option value="FAILED">FAILED</Option>
                                    <Option value="ENROLLED">ENROLLED</Option>
                                    <Option value="UNENROLLED">UNENROLLED</Option>
                                    <Option value="PARTIALLY ENROLLED">PARTIALLY ENROLLED</Option>
                                    <Option value="PARTIALLY FAILED">PARTIALLY FAILED</Option>
                                    <Option value="WAITING FOR ACTION">WAITING FOR ACTION</Option>
                                    <Option value="WAITING FOR PROVIDER">WAITING FOR PROVIDER</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        </Row>
                        <Row gutter={[16,16]}>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }}>
                            <Form.Item name="merchant">
                                <MerchantList placeholder={t("contents.merchants.title")} onChange={onChangeMerchant}
                                    style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }}>
                            <Form.Item name="retailer">
                                <RetailerList placeholder={t("contents.retailers.title")} mid={midSelect} onChange={onChangeRetailer}
                                    style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }}>
                            <Form.Item name="site">
                                <SiteList placeholder={t("contents.sites.title")} mid={midSelect} rid={ridSelect}
                                    style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>
                        </Row>
                        <Row gutter={[16,16]}>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }}>
                            <Form.Item name="field">
                                <Select
                                    showSearch
                                    allowClear
                                    style={{ width: "100%" }}
                                    placeholder={t("tables.fields.field")}
                                >
                                    <Option value="enrollment_request_id">{t("tables.fields.enrollment_request_id")}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }}>
                            <Form.Item name="keyword">
                                <Input placeholder={t("tables.fields.keyword")} />
                            </Form.Item>
                        </Col>
                        </Row>
                        <Row gutter={[16,16]}>
                        <Col flex="auto"></Col>
                        <Col
                            className="gutter-row"
                            xs={{ span: 24 }}
                            sm={{ span: 12 }}
                            md={{ span: 6, offset: 0 }}
                            lg={{ span: 4, offset: 16 }}
                            xl={{ span: 3, offset: GroupAccess("SYSADMIN") ? 0 : 5 }}
                        >
                            <Form.Item>
                                <Button type="secondary" onClick={() => clearAll()} block>
                                    {t("tables.actions.clear_all")}
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 6 }} lg={{ span: 4 }} xl={{ span: 4 }}>
                            <Form.Item>
                                <Button type="primary" htmlType="submit" block>
                                    <SearchOutlined /> {t("tables.actions.search")}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );

    const getDataSource = (datalist) => (datalist && Array.isArray(datalist) ? datalist : []);

    const getHeaderTable = () => (
        <div className="custom-table--header d-flex justify-content-between align-items-center">
            <h3 className="table-title">{t("contents.descriptor_enrollment.title")}</h3>
            <a href={`/adddescriptorenrollment`} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 8 }}>
                <PlusOutlined /> {t("contents.descriptor_enrollment.new_enrollment")}
            </a>
            <ExportModalV3
                t={t}
                keyname="export_all"
                title={t("tables.actions.export")}
                data={requestData}
                data_url={DATA_URL_EXPORT}
                headers={exportHeaders}
                fieldHeadersOptional={exportHeadersOptional}
                filename={`descriptorenrollment_${moment().format("YYMMDD_HHmmss_SSSS")}.csv`}
                style={{ marginLeft: "auto" }}
            />

        </div>
    );

    return (
        <>
            <PageHeader title={t("contents.descriptor_enrollment.title")}></PageHeader>
            {AdvancedSearchForm()}
            <Table
                rowKey="enrollment_request_id"
                size="small"
                title={() => getHeaderTable()}
                loading={isLoading}
                className="tableOverflow table-v3 custom-pagination custom-pagination-v3"
                components={{
                    header: {
                        cell: ColumnResizable,
                    },
                }}
                columns={MergeColumns(columns)}
                scroll={{ x: 1500 }}
                dataSource={getDataSource(data.datalist)}
                onChange={handleChange}
                pagination={CustomPaginationV2({ totalResult, currentPage, requestData, setRequestData, setCurrentPage })}
            />
        </>
    );
};

export default DescriptorEnrollments;
